import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '@utils'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import useFormPersist from 'react-hook-form-persist'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
// Intentionally not importing 'solvpath-microform' at module top — loaded lazily
// from useEffect so bundling failures don't crash the whole checkout route.
import Modal from '@/components/ui/Modal'
import { sessionCartService } from '@/services/campaignService'
import {
  Minus,
  Plus,
  Trash2,
  User,
  CreditCard,
  Lock,
  CircleHelp,
  ArrowRight,
  ShoppingCart,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react'
import '@styles/checkout.css'
import '@styles/payment-info.css'

// Stores and services
import { useSessionStore } from '@stores/useSessionStore'
import { useUIStore } from '@stores/useUIStore'
import { useCartStore } from '@stores/useCartStore'

// Services and utilities
import { paymentService } from '@services/paymentService'

import {
  getFieldLabel,
  getCountries,
  getCountryStates,
  isCountryUSOrCA,
  isFieldRequired,
  formatZipField,
  validateEmail,
  validatePhone,
} from '@utils/validation'
import { validateCardNumber, validateCardExpiry, validateCardCvc, validateCardholderName } from '@utils/cardValidation'
import { formatCardNumber, formatCardExpiry, formatCardCvc } from '@utils/cardFormatting'
import { getCardIconPath, getPlaceholderImageUrl } from '@utils/image'

// Utility functions for price calculation and formatting
const calculatePrice = (item: any): number => {
  if (!item || !item.campaign_product) {
    return 0
  }
  const basePrice = parseFloat(String(item.campaign_product.price)) || 0
  const variantAdjustment = parseFloat(String(item.variant?.price_adjustment)) || 0
  return (basePrice + variantAdjustment) * (item.quantity || 1)
}

const formatCurrency = (amount: string | number, currency: string = 'USD'): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(num || 0)
}

const getItemVariants = (item: any): Array<{ name: string; value: string }> => {
  if (!item?.variant?.options) {
    return []
  }
  return item.variant.options
    .filter((option: any) => !option.is_deleted)
    .map((option: any) => ({
      name: option.name,
      value: option.value,
    }))
}

const PLACEHOLDER_IMAGE = getPlaceholderImageUrl('medium')

const getItemImageUrl = (item: any): string => {
  const cp = item?.campaign_product
  // Order items keep their images on the item's `product`, not on campaign_product,
  // whose `product` is only an id.
  const product = cp?.product ?? item?.product
  return (
    cp?.image?.file?.medium ||
    cp?.image?.file?.original ||
    (typeof cp?.image === 'string' ? cp.image : null) ||
    product?.default_image?.file?.medium ||
    product?.default_image?.file?.original ||
    product?.images?.[0]?.file?.medium ||
    product?.images?.[0]?.file?.original ||
    (typeof product?.images?.[0]?.file === 'string' ? product.images[0].file : null) ||
    PLACEHOLDER_IMAGE
  )
}

const setFallbackImageUrl = (event: React.SyntheticEvent<HTMLImageElement>): void => {
  ;(event.target as HTMLImageElement).src = PLACEHOLDER_IMAGE
}

// Types
interface CreditCard {
  title: string
  number: string
  expiry: string
  expiry_month: number | null
  expiry_year: number | null
  cvv: string
}

interface PaymentSource {
  id: number
  isDefault: boolean
  canUpdate: boolean
  isCard: boolean
  source: string
  type: string
  last4: string
  expDate: string
  // Additional fields from API (optional for compatibility)
  card_type?: string
  last_four?: string
  expiry_month?: number
  expiry_year?: number
  is_default?: boolean
  payment_source?: number
  cc_type?: string
  cc_last4?: string
  cc_exp_date?: string
  can_update?: boolean
}

// Extend CartItem to include missing properties
interface ExtendedCartItem {
  id?: string
  campaign_product: {
    id: number
    name: string
    price: number
    image?: string
    currency?: string
    max_quantity?: number
    bundled_variants?: Array<{
      variant: { id: number; name: string }
      quantity: number
    }>
  }
  variant?: {
    id: number
    name: string
    price_adjustment?: number
    options?: Array<{
      name: string
      value: string
      is_deleted?: boolean
    }>
  }
  quantity: number
  buy_now_clicked?: boolean
}

enum OrderPaymentOptions {
  New = 'new',
  Default = 'default',
}

// Detect card brand from card number (for CVV length validation)
const detectCardBrand = (number: string): string => {
  const n = number.replace(/\D/g, '')
  if (/^4/.test(n)) {
    return 'visa'
  }
  if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) {
    return 'mastercard'
  }
  if (/^3[47]/.test(n)) {
    return 'amex'
  }
  if (/^6(?:011|5)/.test(n)) {
    return 'discover'
  }
  return 'unknown'
}

// Card schema — matches PaymentUpdater's validation logic exactly.
// Uses the shared cardValidation utilities so the two checkout paths stay in sync.
const cardSchema = z
  .object({
    title: z.string(),
    number: z.string(),
    expiry: z.string(),
    cvv: z.string(),
  })
  .superRefine((data, ctx) => {
    const nameResult = validateCardholderName(data.title)
    if (!nameResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: nameResult.error || 'Invalid cardholder name',
        path: ['title'],
      })
    }
    const numberResult = validateCardNumber(data.number)
    if (!numberResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: numberResult.error || 'Invalid card number',
        path: ['number'],
      })
    }
    const expiryResult = validateCardExpiry(data.expiry)
    if (!expiryResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: expiryResult.error || 'Invalid expiration date',
        path: ['expiry'],
      })
    }
    const brand = detectCardBrand(data.number)
    const cvvResult = validateCardCvc(data.cvv, brand)
    if (!cvvResult.isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: cvvResult.error || 'Invalid security code',
        path: ['cvv'],
      })
    }
  })
type CardFormValues = z.infer<typeof cardSchema>

const customerSchema = z
  .object({
    first_name: z
      .string()
      .min(2, 'First name must be at least 2 characters')
      .regex(/^[a-zA-Z\s'-]+$/, 'First name contains invalid characters'),
    last_name: z
      .string()
      .min(2, 'Last name must be at least 2 characters')
      .regex(/^[a-zA-Z\s'-]+$/, 'Last name contains invalid characters'),
    company: z.string().optional().default(''),
    email: z
      .string()
      .min(1, 'Email is required')
      .refine(
        v => validateEmail(v).isValid,
        v => ({ message: validateEmail(v).error || 'Invalid email' })
      ),
    phone: z
      .string()
      .min(1, 'Phone is required')
      .refine(
        v => validatePhone(v).isValid,
        v => ({ message: validatePhone(v).error || 'Invalid phone number' })
      ),
    address1: z.string().min(2, 'Address is required'),
    address2: z.string().optional().default(''),
    city: z.string().min(2, 'City is required'),
    state: z.string().optional().default(''),
    zip: z
      .string()
      .min(3, 'ZIP code is required')
      .regex(/^[a-zA-Z0-9\s-]+$/, 'Invalid ZIP code'),
    country: z.string().min(1, 'Country is required'),
    ship_first_name: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'First name must be at least 2 characters'),
    ship_last_name: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'Last name must be at least 2 characters'),
    ship_address1: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'Address is required'),
    ship_address2: z.string().optional().default(''),
    ship_city: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || v.length >= 2, 'City is required'),
    ship_state: z.string().optional().default(''),
    ship_zip: z
      .string()
      .optional()
      .default('')
      .refine(v => !v || /^[a-zA-Z0-9\s-]*$/.test(v), 'Invalid ZIP code'),
    ship_country: z.string().optional().default('US'),
  })
  .superRefine((data, ctx) => {
    if (isCountryUSOrCA(data.country) && (!data.state || !data.state.trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'State is required', path: ['state'] })
    }
    if (
      data.ship_country &&
      isCountryUSOrCA(data.ship_country) &&
      data.ship_address1 &&
      (!data.ship_state || !data.ship_state.trim())
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'State is required', path: ['ship_state'] })
    }
  })
type CustomerFormValues = z.infer<typeof customerSchema>

export interface CheckoutProps {
  onSubmit: (checkoutInput: any, params: any) => void
  onBack?: () => void // Make onBack optional
  // Renders inside a narrow container (OMP drawer): one column, order summary
  // always collapsible, and no route changes of its own.
  compact?: boolean
  // Stack the form over the summary. Defaults to `compact`; a wide centred modal
  // passes false to keep the two-column desktop layout.
  singleColumn?: boolean
  // The session cart returns campaign_product without a nested product, so an
  // embedded host that already knows the item can resolve its image, and retry
  // other sizes on error the way its own cards do.
  resolveImage?: (item: any) => string | undefined
  onImageError?: (event: React.SyntheticEvent<HTMLImageElement>, item: any) => void
  // The step to read valid_inputs from. Defaults to the store; OMP passes the
  // pinned action step so state.session can stay on the portal step.
  session?: any
  // compact mode routes every would-be navigation here instead of the router.
  onLeave?: (target: 'products' | 'session' | { productId: number | string; slug?: string }) => void
}

const Checkout = ({
  onSubmit,
  compact = false,
  singleColumn = compact,
  resolveImage,
  onImageError,
  session: sessionProp,
  onLeave,
}: CheckoutProps) => {
  // Zustand store hooks
  const { session: storeSession, isSessionUserKnown } = useSessionStore()
  // sessionProp supplies valid_inputs; customer can live on either step.
  const activeStep = sessionProp ?? storeSession
  const session = useMemo(
    () =>
      activeStep
        ? { ...activeStep, customer: (activeStep as any).customer ?? (storeSession as any)?.customer }
        : activeStep,
    [activeStep, storeSession]
  )
  const campaign = useSessionStore(s => s.campaign)
  const { setLoading: setGlobalLoading, setError, setCriticalError } = useUIStore()
  const setLoading = useCallback(
    (value: boolean) => {
      if (!compact) {
        setGlobalLoading(value)
      }
    },
    [compact, setGlobalLoading]
  )
  const isLoading = useUIStore(s => s.isLoading)
  const { cart, shipping, tax, gateway, checkoutErrors, setCheckoutErrors } = useCartStore()

  const routerNavigate = useNavigate()
  const leave = useCallback(
    (target: 'products' | 'session' | { productId: number | string; slug?: string }, opts?: { replace?: boolean }) => {
      if (compact) {
        onLeave?.(target)
        return
      }
      if (target === 'products') {
        routerNavigate('/products')
      } else if (target === 'session') {
        routerNavigate('/session', { replace: !!opts?.replace })
      } else {
        routerNavigate(`/products/${target.productId}/${target.slug || target.productId}`)
      }
    },
    [compact, onLeave, routerNavigate]
  )

  // Remove-item confirmation dialog state
  const [removeIndex, setRemoveIndex] = useState<number | null>(null)
  const [removing, setRemoving] = useState<boolean>(false)

  // Which cart row is currently syncing to the API — shows a spinner + disables buttons
  const [updatingIndex, setUpdatingIndex] = useState<number | null>(null)

  // State management
  const [checkoutInput, setCheckoutInput] = useState<any>(null)
  const [updatedCart, setUpdatedCart] = useState<ExtendedCartItem[]>(
    cart
      ? cart.map(
          item =>
            ({
              ...item,
              campaign_product: {
                ...item.campaign_product,
                currency: (item.campaign_product as any)?.currency || 'USD',
              },
            }) as ExtendedCartItem
        )
      : []
  )

  // Customer / address form — RHF + Zod
  const {
    register: registerCustomer,
    watch: watchCustomer,
    setValue: setCustomerValue,
    trigger: triggerCustomer,
    setError: setCustomerError,
    formState: { errors: customerErrors, isValid: isCustomerValid },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    mode: 'onChange',
    defaultValues: {
      first_name: '',
      last_name: '',
      company: '',
      email: '',
      phone: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      ship_first_name: '',
      ship_last_name: '',
      ship_address1: '',
      ship_address2: '',
      ship_city: '',
      ship_state: '',
      ship_zip: '',
      ship_country: 'US',
    },
  })
  const customer = watchCustomer()

  useFormPersist(compact ? 'omp-checkout-customer' : 'checkout-customer', {
    watch: watchCustomer,
    setValue: setCustomerValue,
    storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
  })

  // Credit card form — RHF + Zod (mirrors PaymentUpdater validation/formatting)
  const {
    watch: watchCard,
    setValue: setCardValue,
    trigger: triggerCard,
    setError: setCardError,
    formState: { errors: cardErrors, isValid: isCardValid },
  } = useForm<CardFormValues>({
    resolver: zodResolver(cardSchema),
    mode: 'onChange',
    defaultValues: {
      title: '',
      number: '',
      expiry: '',
      cvv: '',
    },
  })
  const creditCard = watchCard()
  const cardBrand = detectCardBrand(creditCard.number || '')

  useFormPersist(compact ? 'omp-checkout-card' : 'checkout-card', {
    watch: watchCard,
    setValue: setCardValue,
    storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
    exclude: ['cvv'],
  })

  // UI state
  const [showOrderSummary, setShowOrderSummary] = useState<boolean>(false)
  // fieldErrors removed — validation via customerErrors (RHF + Zod)
  const [showShipEdit, setShowShipEdit] = useState<boolean>(true)
  const [showBillEdit, setShowBillEdit] = useState<boolean>(true)
  const [showSourceEdit, setShowSourceEdit] = useState<boolean>(true)
  const [billShipSame, setBillShipSame] = useState<boolean>(true)

  // Payment source state
  const [orderPaymentSource, setOrderPaymentSource] = useState<OrderPaymentOptions>(OrderPaymentOptions.New)
  const [paymentSource, setPaymentSource] = useState<PaymentSource | null>(null)
  const [paymentSourceOptions, setPaymentSourceOptions] = useState<PaymentSource[]>([])

  // Computed values
  const totalPrice = cart
    ? cart
        .reduce((total, item) => {
          return total + calculatePrice(item)
        }, 0)
        .toFixed(2)
    : '0.00'

  const totalPriceIncTax = () => {
    let total = parseFloat(totalPrice) + parseFloat(tax?.toString() || '0')
    if (shipping && shipping > 0) {
      total += shipping
    }
    return total.toFixed(2)
  }

  const currency = updatedCart.length > 0 ? updatedCart[0].campaign_product.currency || 'USD' : 'USD'

  const isCustomerUpdated = (): boolean => {
    if (!session?.customer) {
      return false
    }
    return Object.entries(customer).some(([key, value]) => {
      const sessionCustomer = session.customer as any
      return value !== sessionCustomer[key]
    })
  }

  const guaranteeDays = (campaign as any)?.guarantee_days ?? 0
  const showSatisfactionGuarantee = guaranteeDays > 0

  // Utility functions
  const getPrice = (item: any) => {
    const price = calculatePrice(item)
    return price !== null && !isNaN(price) ? price.toFixed(2) : '0'
  }

  const getImageUrl = (item: any) => {
    return resolveImage?.(item) || getItemImageUrl(item)
  }

  const setAltImg = (event: React.SyntheticEvent<HTMLImageElement>, item?: any) => {
    if (onImageError) {
      onImageError(event, item)
      return
    }
    setFallbackImageUrl(event)
  }

  const validateBillingFields = () => {
    const fields: Array<keyof CustomerFormValues> = [
      'first_name',
      'last_name',
      'email',
      'phone',
      'address1',
      'city',
      'zip',
      'country',
    ]
    if (isCountryUSOrCA(customer.country)) {
      fields.push('state')
    }
    if (isFieldRequired('company', customer.country)) {
      fields.push('company')
    }
    return triggerCustomer(fields)
  }

  const validateShippingFields = () => {
    if (billShipSame) {
      return Promise.resolve(true)
    }
    const fields: Array<keyof CustomerFormValues> = [
      'ship_first_name',
      'ship_last_name',
      'ship_address1',
      'ship_city',
      'ship_zip',
      'ship_country',
    ]
    if (isCountryUSOrCA(customer.ship_country)) {
      fields.push('ship_state')
    }
    return triggerCustomer(fields)
  }

  const validateCreditCardInfo = async (): Promise<boolean> => {
    if (orderPaymentSource !== OrderPaymentOptions.New) {
      return true
    }
    return triggerCard()
  }

  const updateShippingAddress = useCallback(
    (copyFromBilling: boolean) => {
      if (!copyFromBilling) {
        return
      }
      const pairs = ['address1', 'address2', 'city', 'state', 'zip', 'first_name', 'last_name', 'country'] as const
      pairs.forEach(f => {
        const value = customer[f as keyof CustomerFormValues] as string | undefined
        setCustomerValue(`ship_${f}` as keyof CustomerFormValues, value || '')
      })
    },
    [customer, setCustomerValue]
  )

  const toggleEditing = async (name: string) => {
    switch (name) {
      case 'ship':
        if (showShipEdit && (await validateShippingFields())) {
          setShowShipEdit(false)
        } else if (!showShipEdit) {
          setShowShipEdit(true)
        }
        break
      case 'bill':
        if (showBillEdit && (await validateBillingFields())) {
          setShowBillEdit(false)
        } else if (!showBillEdit) {
          setShowBillEdit(true)
        }
        break
      case 'source':
        if (showSourceEdit) {
          if (await validateCreditCardInfo()) {
            setShowSourceEdit(false)
          }
        } else if (!showSourceEdit) {
          setShowSourceEdit(true)
        }
        break
    }
  }

  // Cart store actions — used by updateQuantity / remove flows
  const { setCart, updateSessionCart } = useCartStore()

  const updateQuantity = async (index: number, quantity: number) => {
    const maxQuantity = updatedCart[index]?.campaign_product?.max_quantity || 999
    const clamped = Math.max(1, Math.min(quantity, maxQuantity))

    const newCart = updatedCart.map((item, i) => (i === index ? { ...item, quantity: clamped } : item))
    setUpdatedCart(newCart)
    // Sync to store immediately so store subscribers see the change
    setCart(newCart)

    if (!session?.id) {
      return
    }
    // Show spinner next to the qty controls while the cart API is in flight
    setUpdatingIndex(index)
    try {
      await updateSessionCart(session.id, {
        ship_city: customer.ship_city,
        ship_state: customer.ship_state,
        ship_zip: customer.ship_zip,
      })
    } catch {
      /* surface handled elsewhere */
    } finally {
      setUpdatingIndex(null)
    }
  }

  // Open confirmation dialog for removing an item
  const handleRemoveClick = (index: number) => {
    setRemoveIndex(index)
  }

  // Confirmed removal — filter the item; if cart becomes empty, clear + go back to products
  const confirmRemove = async () => {
    if (removeIndex === null || removing) {
      return
    }
    const idx = removeIndex
    const isLast = updatedCart.length <= 1

    const newCart = updatedCart.filter((_, i) => i !== idx)
    setUpdatedCart(newCart)
    setCart(newCart)

    if (isLast) {
      // Last item — keep the modal open showing the loader while we clear + redirect
      setRemoving(true)
      useCartStore.getState().clearCart()
      if (session?.id) {
        try {
          await sessionCartService.clearSessionCart(session.id)
        } catch {
          /* best-effort */
        }
      }
      leave('products')
      // Component will unmount — no need to reset state
      return
    }

    // Non-last item — close the modal immediately, sync the cart in the background
    setRemoveIndex(null)
    if (session?.id) {
      updateSessionCart(session.id, {
        ship_city: customer.ship_city,
        ship_state: customer.ship_state,
        ship_zip: customer.ship_zip,
      }).catch(() => {
        /* best-effort */
      })
    }
  }

  const countryChanged = () => {
    setCustomerValue('state', '')
    setCustomerValue('zip', '')
  }

  const shipCountryChanged = () => {
    setCustomerValue('ship_state', '')
    setCustomerValue('ship_zip', '')
  }

  const zipFormatHandler = (value: string, isShip: boolean = false) => {
    const country = isShip ? customer.ship_country : customer.country
    return formatZipField(value, country)
  }

  // Checkout submission
  const handleCheckout = async () => {
    const isBillingValid = await validateBillingFields()
    const isShippingValid = await validateShippingFields()
    const isCreditCardValid = await validateCreditCardInfo()

    if (!isShippingValid) {
      setShowShipEdit(true)
    }
    if (!isBillingValid) {
      setShowBillEdit(true)
    }
    if (!isBillingValid || !isShippingValid || !isCreditCardValid) {
      return
    }

    const cartData = cart?.map(item => {
      let bundledVariants = null
      const extendedItem = item as ExtendedCartItem
      if (extendedItem.campaign_product.bundled_variants?.length) {
        bundledVariants = extendedItem.campaign_product.bundled_variants.map(bundle => ({
          variant: bundle.variant.id,
          quantity: bundle.quantity,
        }))
      }

      return {
        campaign_product: item.campaign_product.id,
        variant: item.variant?.id || null,
        quantity: item.quantity,
        bundled_variants: bundledVariants,
      }
    })

    const params: any = { items: cartData }

    if (isSessionUserKnown() && isCustomerUpdated()) {
      params.customer = customer
    } else if (!isSessionUserKnown()) {
      params.customer = customer
    }

    if (orderPaymentSource === OrderPaymentOptions.New) {
      const [expMonthStr, expYearStr] = (creditCard.expiry || '').split('/')
      let exp_year = (expYearStr || '').trim()
      if (exp_year.length === 2 && /^\d+$/.test(exp_year)) {
        exp_year = new Date().getFullYear().toString().slice(0, 2) + exp_year
      }
      const rawMonth = (expMonthStr || '').trim()
      const exp_month = /^\d+$/.test(rawMonth) ? rawMonth.padStart(2, '0') : rawMonth
      params.card = {
        title: creditCard.title,
        number: creditCard.number.replace(/\s/g, ''),
        exp_month,
        exp_year,
        cvv_code: creditCard.cvv,
      }
    }

    if (gateway) {
      params.gateway = gateway.id
    }

    setCheckoutErrors(null)
    onSubmit(checkoutInput, params)
  }

  // Effects
  useEffect(() => {
    useCartStore.getState().setCartOpen(false)
  }, [])

  useEffect(() => {
    if (session?.valid_inputs?.[0]) {
      setCheckoutInput(session.valid_inputs[0])
    }
  }, [session])

  useEffect(() => {
    if (!cart || cart.length) {
      return
    }
    if (!(session as any)?.is_legacy) {
      const goHomeAndNavigate = async () => {
        try {
          await useSessionStore.getState().goHome()
        } finally {
          leave('session', { replace: true })
        }
      }
      goHomeAndNavigate()
    } else {
      leave('products')
    }
  }, [cart?.length])

  useEffect(() => {
    setUpdatedCart(
      cart
        ? cart.map(
            item =>
              ({
                ...item,
                campaign_product: {
                  ...item.campaign_product,
                  currency: (item.campaign_product as any)?.currency || 'USD',
                },
              }) as ExtendedCartItem
          )
        : []
    )
  }, [cart])

  useEffect(() => {
    if (billShipSame && !isSessionUserKnown()) {
      updateShippingAddress(true)
    }
  }, [billShipSame, customer.address1, customer.city, customer.state, customer.zip, customer.country])

  useEffect(() => {
    if (isSessionUserKnown() && session?.customer) {
      const sc = session.customer as any
      const fields: Array<keyof CustomerFormValues> = [
        'first_name',
        'last_name',
        'company',
        'email',
        'phone',
        'address1',
        'address2',
        'city',
        'state',
        'zip',
        'country',
        'ship_first_name',
        'ship_last_name',
        'ship_address1',
        'ship_address2',
        'ship_city',
        'ship_state',
        'ship_zip',
        'ship_country',
      ]
      fields.forEach(key => {
        const value = sc[key]
        if (key === 'country' || key === 'ship_country') {
          setCustomerValue(key, value?.toUpperCase() || 'US')
        } else {
          setCustomerValue(key, value || '')
        }
      })
      // Re-run validation across the form so isValid flips true and the
      // Complete-Order button enables. setValue alone does not validate.
      triggerCustomer()

      setBillShipSame(false)
      setShowShipEdit(false)
      setShowBillEdit(false)
      setShowSourceEdit(false)
      setLoading(true)

      const loadPaymentSources = async () => {
        try {
          const paymentSources = await paymentService.getPaymentSources()
          setPaymentSource(null)
          let source = paymentSources.find(ps => ps.is_default)
          if (!source && paymentSources.length >= 1) {
            source = paymentSources[0]
          }

          if (source) {
            setOrderPaymentSource(OrderPaymentOptions.Default)
            setPaymentSource({
              id: source.id,
              isDefault: source.is_default || false,
              canUpdate: source.can_update,
              isCard: true,
              source: 'Credit Card',
              type: source.cc_type || 'unknown',
              last4: source.cc_last4 || '****',
              expDate: source.cc_exp_date || '',
            })
            setPaymentSourceOptions([
              {
                id: source.id,
                isDefault: source.is_default || false,
                canUpdate: source.can_update,
                isCard: true,
                source: 'Credit Card',
                type: source.cc_type || 'unknown',
                last4: source.cc_last4 || '****',
                expDate: source.cc_exp_date || '',
                card_type: source.cc_type,
                last_four: source.cc_last4,
                expiry_month: source.cc_exp_date ? Number(source.cc_exp_date.split('/')[0]) : undefined,
                expiry_year: source.cc_exp_date ? Number(source.cc_exp_date.split('/')[1]) : undefined,
                is_default: source.is_default,
              },
            ])
          } else {
            setShowSourceEdit(true)
          }
          setLoading(false)
        } catch (err) {
          setCriticalError(err)
        }
      }
      loadPaymentSources()
    }
  }, [session])

  useEffect(() => {
    if (!checkoutErrors?.length) {
      return
    }
    const shippingFields = ['ship_address1', 'ship_city', 'ship_first_name', 'ship_last_name', 'ship_state', 'ship_zip']
    const billingFields = ['address1', 'city', 'state', 'zip', 'first_name', 'last_name', 'email', 'phone']
    const customerFields = [...billingFields, ...shippingFields] as Array<keyof CustomerFormValues>

    const errorFields: Partial<Record<keyof CustomerFormValues, string>> = {}
    ;(checkoutErrors as any[]).forEach((error: any) => {
      Object.keys(error).forEach(field => {
        if (customerFields.includes(field as keyof CustomerFormValues)) {
          const raw = error[field]
          const message = Array.isArray(raw) ? raw.join(', ') : String(raw)
          errorFields[field as keyof CustomerFormValues] = message
        }
        if (!isSessionUserKnown()) {
          if (billingFields.includes(field)) {
            setShowBillEdit(true)
          }
          if (shippingFields.includes(field)) {
            setShowShipEdit(true)
          }
        }
      })
    })

    const matchedKeys = Object.keys(errorFields) as Array<keyof CustomerFormValues>
    if (matchedKeys.length) {
      matchedKeys.forEach(field => {
        setCustomerError(field, { type: 'server', message: errorFields[field] })
      })
    } else {
      setError(checkoutErrors)
    }
  }, [checkoutErrors])

  // Card validation is handled by RHF + Zod schema (mode: 'onChange')
  // using validateCardNumber / validateCardExpiry / validateCardCvc from
  // @utils/cardValidation — same utilities PaymentUpdater uses.

  useEffect(() => {
    setShowSourceEdit(orderPaymentSource === OrderPaymentOptions.New)
  }, [orderPaymentSource])

  // Cybersource microform auto-intercept — imports 'solvpath-microform' and tokenizes
  // card data before submit so raw PAN never leaves the browser. The SDK attaches
  // a handler to #checkout-submit-btn.
  useEffect(() => {
    const publicKey = gateway?.public_key
    if (!publicKey || orderPaymentSource !== OrderPaymentOptions.New) {
      return
    }

    let destroyed = false
    let instance: any = null

    ;(async () => {
      try {
        await import('solvpath-microform')
        if (destroyed) {
          return
        }
        const factory = (window as any).solvpathMicroform
        const submitMode = (window as any).SolvpathSubmitMode?.AUTO_INTERCEPT
        if (typeof factory !== 'function') {
          console.warn('[checkout] solvpath-microform loaded but window.solvpathMicroform missing')
          return
        }
        instance = factory({
          apiKey: publicKey,
          submitId: 'checkout-submit-btn',
          cardFieldId: 'number',
          cvvFieldId: 'cvv_code',
          expiryId: 'expiry',
          expiryMonthId: 'expiry_month',
          expiryYearId: 'expiry_year',
          submitMode,
          errorModal: (message: string) => {
            setCardError('number', { type: 'server', message })
            setError([message])
          },
        })
      } catch (err) {
        console.warn(
          '[checkout] solvpath-microform SDK unavailable — falling back to standard submit. Install `solvpath-microform` to enable tokenization.',
          err
        )
      }
    })()

    return () => {
      destroyed = true
      if (instance?.destroy) {
        instance.destroy()
      }
    }
  }, [gateway, orderPaymentSource, setError, setCardError])

  const completeOrderBlock = (
    <div className={compact ? 'checkout-submit-standalone' : 'form-section'}>
      <button
        type="button"
        id="checkout-submit-btn"
        className="btn-base complete-order-btn"
        onClick={handleCheckout}
        disabled={!isCustomerValid || (orderPaymentSource === OrderPaymentOptions.New && !isCardValid) || isLoading}>
        <Lock size={18} className="complete-order-lock" aria-hidden />
        <span>Complete My Order</span>
        <ArrowRight size={20} className="complete-order-arrow" aria-hidden />
      </button>
    </div>
  )

  return (
    <div className={cn('checkout-container', compact && 'checkout-compact')}>
      {/* Mobile Order Summary — collapsible bar with always-visible grand total below */}
      <div className={cn('mobile-summary', !singleColumn && 'md:hidden')}>
        <button
          type="button"
          className="mobile-summary-toggle"
          onClick={() => setShowOrderSummary(!showOrderSummary)}
          aria-expanded={showOrderSummary}>
          <span className="mobile-summary-toggle-left">
            <ShoppingCart size={18} />
            <span>{showOrderSummary ? 'Hide order summary' : 'Show order summary'}</span>
            {showOrderSummary ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </button>

        {showOrderSummary && (
          <div className="mobile-summary-content">
            {updatedCart.map((item, index) => {
              const max = (item as ExtendedCartItem).campaign_product.max_quantity || 999
              return (
                <div key={index} className="mobile-order-item">
                  <div className="mobile-order-item-img">
                    <img src={getImageUrl(item)} alt={item.campaign_product.name} onError={e => setAltImg(e, item)} />
                    <span className="mobile-qty-badge">{item.quantity}</span>
                  </div>
                  <div className="mobile-order-item-info">
                    <h6
                      className={compact ? undefined : 'product-name-link'}
                      onClick={
                        compact
                          ? undefined
                          : () => {
                              const cp = item.campaign_product as any
                              if (cp?.id) {
                                leave({ productId: cp.id, slug: cp.slug })
                              }
                            }
                      }>
                      {item.campaign_product.name}
                    </h6>
                    {getItemVariants(item).map((variant, vIndex) => (
                      <div key={vIndex} className="mobile-order-item-variant">
                        {variant.name}: {variant.value}
                      </div>
                    ))}
                    <div className="mobile-order-item-controls">
                      <div className="qty-pill mobile-qty-pill">
                        <button
                          type="button"
                          className="no-transform"
                          disabled={item.quantity <= 1 || updatingIndex === index}
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          aria-label="Decrease quantity">
                          <Minus size={13} />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          className="no-transform"
                          disabled={item.quantity >= max || updatingIndex === index}
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          aria-label="Increase quantity">
                          <Plus size={13} />
                        </button>
                      </div>
                      {updatingIndex === index && (
                        <Loader2
                          size={16}
                          className="animate-spin"
                          style={{ color: 'var(--light-text-color, #6b7280)' }}
                        />
                      )}
                      <button
                        type="button"
                        className="no-transform mobile-order-item-remove"
                        onClick={() => handleRemoveClick(index)}
                        disabled={updatingIndex === index}
                        aria-label="Remove item">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="mobile-order-item-price">{formatCurrency(getPrice(item), currency)}</div>
                </div>
              )
            })}

            <div className="mobile-summary-totals">
              <div className="mobile-summary-row">
                <span>Subtotal</span>
                <span>{formatCurrency(totalPrice, currency)}</span>
              </div>
              <div className="mobile-summary-row">
                <span>Shipping fees</span>
                <span>{Number(shipping) > 0 ? formatCurrency(Number(shipping).toString(), currency) : 'Free'}</span>
              </div>
              <div className="mobile-summary-row">
                <span>Taxes</span>
                <span>
                  {Number(tax) > 0 ? formatCurrency(Number(tax).toString(), currency) : formatCurrency('0', currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Grand Total always visible — whether expanded or collapsed */}
        <div className="mobile-summary-grand">
          <strong>
            Grand Total <span className="font-normal">(including tax)</span>
          </strong>
          <strong>{formatCurrency(totalPriceIncTax(), currency)}</strong>
        </div>
      </div>

      <div className={cn('flex flex-wrap w-full', !singleColumn && 'md:flex-nowrap md:gap-x-6 lg:gap-x-8')}>
        <div className={cn('w-full', !singleColumn && 'md:w-[55%]')}>
          {/* Main Checkout Form */}
          <div className="checkout-form">
            <div className="form-section">
              <div className="flex justify-between items-center mb-4">
                <h4 className="m-0!">Billing Information</h4>
                {!showBillEdit && (
                  <button
                    type="button"
                    className="btn-bare checkout-change-button"
                    onClick={() => setShowBillEdit(true)}>
                    EDIT
                  </button>
                )}
              </div>

              {!showBillEdit ? (
                <div className="checkout-info-card">
                  <p>
                    {customer.first_name} {customer.last_name}
                    <br />
                    {customer.address1}
                    {customer.address2 ? (
                      <>
                        <br />
                        {customer.address2}
                      </>
                    ) : null}
                    <br />
                    {customer.city}
                    {customer.state ? `, ${customer.state}` : ''}, {customer.country}, {customer.zip}
                    <br />
                    {customer.email}
                    <br />
                    {customer.phone}
                  </p>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                    <div className="form-group">
                      <input
                        type="text"
                        className={`form-control ${customerErrors.first_name ? 'is-invalid' : ''}`}
                        placeholder={getFieldLabel('first_name', 'First Name', customer.country)}
                        value={customer.first_name}
                        onChange={e => setCustomerValue('first_name', e.target.value)}
                      />
                      {customerErrors.first_name && (
                        <div className="invalid-feedback">{customerErrors.first_name.message}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        className={`form-control ${customerErrors.last_name ? 'is-invalid' : ''}`}
                        placeholder={getFieldLabel('last_name', 'Last Name', customer.country)}
                        value={customer.last_name}
                        onChange={e => setCustomerValue('last_name', e.target.value)}
                      />
                      {customerErrors.last_name && (
                        <div className="invalid-feedback">{customerErrors.last_name.message}</div>
                      )}
                    </div>
                  </div>

                  {isFieldRequired('company', customer.country) && (
                    <div className="form-group">
                      <input
                        type="text"
                        className={`form-control ${customerErrors.company ? 'is-invalid' : ''}`}
                        placeholder={getFieldLabel('company', 'Company', customer.country)}
                        value={customer.company || ''}
                        onChange={e => setCustomerValue('company', e.target.value)}
                      />
                      {customerErrors.company && (
                        <div className="invalid-feedback">{customerErrors.company.message}</div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                    <div className="form-group">
                      <input
                        type="email"
                        className={`form-control ${customerErrors.email ? 'is-invalid' : ''}`}
                        placeholder="Email"
                        value={customer.email}
                        onChange={e => setCustomerValue('email', e.target.value)}
                      />
                      {customerErrors.email && <div className="invalid-feedback">{customerErrors.email.message}</div>}
                    </div>
                    <div className="form-group">
                      <input
                        type="tel"
                        inputMode="numeric"
                        className={`form-control ${customerErrors.phone ? 'is-invalid' : ''}`}
                        placeholder="Phone"
                        value={customer.phone}
                        onChange={e => {
                          const digits = e.target.value.replace(/[^0-9+\-() ]/g, '')
                          setCustomerValue('phone', digits, { shouldValidate: true })
                        }}
                      />
                      {customerErrors.phone && <div className="invalid-feedback">{customerErrors.phone.message}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      className={`form-control ${customerErrors.address1 ? 'is-invalid' : ''}`}
                      placeholder={getFieldLabel('address1', 'Address', customer.country)}
                      value={customer.address1}
                      onChange={e => setCustomerValue('address1', e.target.value)}
                    />
                    {customerErrors.address1 && (
                      <div className="invalid-feedback">{customerErrors.address1.message}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={getFieldLabel('address2', 'Apartment, suite, etc. (optional)', customer.country)}
                      value={customer.address2 || ''}
                      onChange={e => setCustomerValue('address2', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-x-3">
                    <div className="form-group sm:col-span-2">
                      <input
                        type="text"
                        className={`form-control ${customerErrors.city ? 'is-invalid' : ''}`}
                        placeholder={getFieldLabel('city', 'City', customer.country)}
                        value={customer.city}
                        onChange={e => setCustomerValue('city', e.target.value)}
                      />
                      {customerErrors.city && <div className="invalid-feedback">{customerErrors.city.message}</div>}
                    </div>
                    {isCountryUSOrCA(customer.country) ? (
                      <div className="form-group">
                        <select
                          className={`form-control ${customerErrors.state ? 'is-invalid' : ''}`}
                          value={customer.state}
                          onChange={e => setCustomerValue('state', e.target.value)}>
                          <option value="">{getFieldLabel('state', 'State', customer.country)}</option>
                          {getCountryStates(customer.country).map(state => (
                            <option key={state.value} value={state.value}>
                              {state.text}
                            </option>
                          ))}
                        </select>
                        {customerErrors.state && <div className="invalid-feedback">{customerErrors.state.message}</div>}
                      </div>
                    ) : (
                      <div className="form-group">
                        <input
                          type="text"
                          className={`form-control ${customerErrors.state ? 'is-invalid' : ''}`}
                          placeholder="State / Province"
                          value={customer.state}
                          onChange={e => setCustomerValue('state', e.target.value)}
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <input
                        type="text"
                        className={`form-control ${customerErrors.zip ? 'is-invalid' : ''}`}
                        placeholder={getFieldLabel('zip', 'ZIP code', customer.country)}
                        value={customer.zip}
                        onChange={e => setCustomerValue('zip', zipFormatHandler(e.target.value))}
                      />
                      {customerErrors.zip && <div className="invalid-feedback">{customerErrors.zip.message}</div>}
                    </div>
                  </div>

                  <div className="form-group">
                    <select
                      className="form-control"
                      value={customer.country}
                      onChange={e => {
                        setCustomerValue('country', e.target.value)
                        countryChanged()
                      }}>
                      {getCountries().map(country => (
                        <option key={country.value} value={country.value}>
                          {country.text}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <div className="flex justify-between items-center mb-4">
                <h4 className="m-0!">Shipping Information</h4>
                {!showShipEdit && !billShipSame && (
                  <button
                    type="button"
                    className="btn-bare checkout-change-button"
                    onClick={() => setShowShipEdit(true)}>
                    EDIT
                  </button>
                )}
              </div>

              {!showShipEdit && !billShipSame ? (
                <div className="checkout-info-card">
                  <p>
                    {customer.ship_first_name} {customer.ship_last_name}
                    <br />
                    {customer.ship_address1}
                    {customer.ship_address2 ? (
                      <>
                        <br />
                        {customer.ship_address2}
                      </>
                    ) : null}
                    <br />
                    {customer.ship_city}
                    {customer.ship_state ? `, ${customer.ship_state}` : ''}, {customer.ship_country},{' '}
                    {customer.ship_zip}
                  </p>
                </div>
              ) : (
                <>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="shipSameAsBilling"
                      checked={billShipSame}
                      onChange={e => setBillShipSame(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="shipSameAsBilling">
                      Shipping same as billing information
                    </label>
                  </div>

                  {!billShipSame && (
                    <div className="mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                        <div className="form-group">
                          <input
                            type="text"
                            className={`form-control ${customerErrors.ship_first_name ? 'is-invalid' : ''}`}
                            placeholder={getFieldLabel('ship_first_name', 'First name', customer.ship_country, true)}
                            value={customer.ship_first_name}
                            onChange={e => setCustomerValue('ship_first_name', e.target.value)}
                          />
                          {customerErrors.ship_first_name && (
                            <div className="invalid-feedback">{customerErrors.ship_first_name.message}</div>
                          )}
                        </div>
                        <div className="form-group">
                          <input
                            type="text"
                            className={`form-control ${customerErrors.ship_last_name ? 'is-invalid' : ''}`}
                            placeholder={getFieldLabel('ship_last_name', 'Last name', customer.ship_country, true)}
                            value={customer.ship_last_name}
                            onChange={e => setCustomerValue('ship_last_name', e.target.value)}
                          />
                          {customerErrors.ship_last_name && (
                            <div className="invalid-feedback">{customerErrors.ship_last_name.message}</div>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <input
                          type="text"
                          className={`form-control ${customerErrors.ship_address1 ? 'is-invalid' : ''}`}
                          placeholder={getFieldLabel('ship_address1', 'Address', customer.ship_country, true)}
                          value={customer.ship_address1}
                          onChange={e => setCustomerValue('ship_address1', e.target.value)}
                        />
                        {customerErrors.ship_address1 && (
                          <div className="invalid-feedback">{customerErrors.ship_address1.message}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={getFieldLabel(
                            'ship_address2',
                            'Apartment, suite, etc. (optional)',
                            customer.ship_country,
                            true
                          )}
                          value={customer.ship_address2 || ''}
                          onChange={e => setCustomerValue('ship_address2', e.target.value)}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-x-3">
                        <div className="form-group sm:col-span-2">
                          <input
                            type="text"
                            className={`form-control ${customerErrors.ship_city ? 'is-invalid' : ''}`}
                            placeholder={getFieldLabel('ship_city', 'City', customer.ship_country, true)}
                            value={customer.ship_city}
                            onChange={e => setCustomerValue('ship_city', e.target.value)}
                          />
                          {customerErrors.ship_city && (
                            <div className="invalid-feedback">{customerErrors.ship_city.message}</div>
                          )}
                        </div>
                        {isCountryUSOrCA(customer.ship_country) && (
                          <div className="form-group">
                            <select
                              className={`form-control ${customerErrors.ship_state ? 'is-invalid' : ''}`}
                              value={customer.ship_state}
                              onChange={e => setCustomerValue('ship_state', e.target.value)}>
                              <option value="">
                                {getFieldLabel('ship_state', 'State', customer.ship_country, true)}
                              </option>
                              {getCountryStates(customer.ship_country).map(state => (
                                <option key={state.value} value={state.value}>
                                  {state.text}
                                </option>
                              ))}
                            </select>
                            {customerErrors.ship_state && (
                              <div className="invalid-feedback">{customerErrors.ship_state.message}</div>
                            )}
                          </div>
                        )}
                        <div className="form-group">
                          <input
                            type="text"
                            className={`form-control ${customerErrors.ship_zip ? 'is-invalid' : ''}`}
                            placeholder={getFieldLabel('ship_zip', 'ZIP code', customer.ship_country, true)}
                            value={customer.ship_zip}
                            onChange={e => setCustomerValue('ship_zip', zipFormatHandler(e.target.value, true))}
                          />
                          {customerErrors.ship_zip && (
                            <div className="invalid-feedback">{customerErrors.ship_zip.message}</div>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <select
                          className="form-control"
                          value={customer.ship_country}
                          onChange={e => {
                            setCustomerValue('ship_country', e.target.value)
                            shipCountryChanged()
                          }}>
                          {getCountries().map(country => (
                            <option key={country.value} value={country.value}>
                              {country.text}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Payment Method */}
            <div className="form-section">
              <h4>Payment Method</h4>

              {isSessionUserKnown() && paymentSourceOptions.length > 0 && (
                <div className="flex flex-col gap-3 mb-4">
                  <label
                    htmlFor="existingCard"
                    className="payment-card-option"
                    data-selected={orderPaymentSource === OrderPaymentOptions.Default || undefined}>
                    <input
                      className="form-check-input"
                      type="radio"
                      id="existingCard"
                      value={OrderPaymentOptions.Default}
                      checked={orderPaymentSource === OrderPaymentOptions.Default}
                      onChange={e => setOrderPaymentSource(e.target.value as OrderPaymentOptions)}
                    />
                    <img
                      src={getCardIconPath(paymentSource?.type || 'unknown')}
                      alt={paymentSource?.type || 'Card'}
                      className="w-10 h-auto"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold" style={{ color: 'var(--text-color)' }}>
                        **** **** **** {paymentSource?.last4}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--light-text-color)' }}>
                        {(paymentSource?.type || 'Card').charAt(0).toUpperCase() +
                          (paymentSource?.type || 'card').slice(1)}{' '}
                        · Expires {paymentSource?.expDate}
                      </div>
                    </div>
                  </label>

                  <button
                    type="button"
                    className="payment-card-add"
                    onClick={() => setOrderPaymentSource(OrderPaymentOptions.New)}>
                    <Plus size={16} />
                    <span>Add New Card</span>
                  </button>
                </div>
              )}

              {showSourceEdit && (
                <div className="credit-card-form">
                  {/* Name on Card — with user icon prefix */}
                  <div className="form-group">
                    <div className="payment-input-wrap">
                      <span className="payment-input-prefix">
                        <User size={18} />
                      </span>
                      <input
                        type="text"
                        className={`form-control payment-input ${cardErrors.title ? 'is-invalid' : ''}`}
                        placeholder="Name on Card"
                        value={creditCard.title || ''}
                        onChange={e => setCardValue('title', e.target.value, { shouldValidate: true })}
                      />
                    </div>
                    {cardErrors.title && <div className="invalid-feedback">{cardErrors.title.message}</div>}
                  </div>

                  {/* Card Number — auto-formatted with spaces + card icon + lock suffix */}
                  <div className="form-group">
                    <div className="payment-input-wrap">
                      <span className="payment-input-prefix">
                        <CreditCard size={18} />
                      </span>
                      <input
                        id="number"
                        type="tel"
                        inputMode="numeric"
                        className={`form-control payment-input payment-input-both ${cardErrors.number ? 'is-invalid' : ''}`}
                        placeholder="1234 1234 1234 1234"
                        maxLength={23}
                        value={creditCard.number || ''}
                        onChange={e =>
                          setCardValue('number', formatCardNumber(e.target.value), { shouldValidate: true })
                        }
                      />
                      <span className="payment-input-suffix text-gray-400 cursor-help" role="tooltip">
                        <Lock size={16} />
                        <span className="tooltip-bubble">All payments are safe and secure</span>
                      </span>
                    </div>
                    {cardErrors.number && <div className="invalid-feedback">{cardErrors.number.message}</div>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                    <div className="form-group">
                      <input
                        id="expiry"
                        type="tel"
                        inputMode="numeric"
                        className={`form-control ${cardErrors.expiry ? 'is-invalid' : ''}`}
                        placeholder="MM/YYYY"
                        maxLength={7}
                        value={creditCard.expiry || ''}
                        onChange={e =>
                          setCardValue('expiry', formatCardExpiry(e.target.value), { shouldValidate: true })
                        }
                      />
                      <input
                        type="hidden"
                        id="expiry_month"
                        readOnly
                        value={(() => {
                          const m = (creditCard.expiry || '').split('/')[0]?.trim() || ''
                          return /^\d+$/.test(m) ? m.padStart(2, '0') : m
                        })()}
                      />
                      <input
                        type="hidden"
                        id="expiry_year"
                        readOnly
                        value={(() => {
                          let y = (creditCard.expiry || '').split('/')[1]?.trim() || ''
                          if (y.length === 2 && /^\d+$/.test(y)) {
                            y = new Date().getFullYear().toString().slice(0, 2) + y
                          }
                          return y
                        })()}
                      />
                      {cardErrors.expiry && <div className="invalid-feedback">{cardErrors.expiry.message}</div>}
                    </div>
                    <div className="form-group">
                      <div className="payment-input-wrap">
                        <input
                          id="cvv_code"
                          type="tel"
                          inputMode="numeric"
                          className={`form-control payment-input-suffix-pad ${cardErrors.cvv ? 'is-invalid' : ''}`}
                          placeholder={cardBrand === 'amex' ? 'CVV (4 digits)' : 'CVV'}
                          maxLength={cardBrand === 'amex' ? 4 : 3}
                          value={creditCard.cvv || ''}
                          onChange={e =>
                            setCardValue('cvv', formatCardCvc(e.target.value, cardBrand), { shouldValidate: true })
                          }
                        />
                        <span className="payment-input-suffix text-gray-400 cursor-help" role="tooltip">
                          <CircleHelp size={16} />
                          <span className="tooltip-bubble">3 or 4 digits usually found on the back of your card</span>
                        </span>
                      </div>
                      {cardErrors.cvv && <div className="invalid-feedback">{cardErrors.cvv.message}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {showSatisfactionGuarantee && (
              <div className="icon-guarantee">
                <div className="logo">
                  <img
                    src="/assets/images/guarantee-icon.png"
                    alt="Satisfaction Guarantee"
                    className="max-w-full h-auto"
                  />
                </div>
                <div>
                  <h5 className="satisfaction-text">{guaranteeDays} Day Satisfaction Guarantee</h5>
                  <div className="guarantee-text">
                    We stand behind our products with a {guaranteeDays} day satisfaction guarantee. If you're not
                    completely satisfied, return your purchase for a full refund.
                  </div>
                </div>
              </div>
            )}

            {/* Complete Order Button — only an embedded two-column host moves it out */}
            {!(compact && !singleColumn) && completeOrderBlock}
          </div>
        </div>

        <div className={cn('w-full', !singleColumn && 'md:w-[45%]')}>
          {/* Desktop Order Summary */}
          <div className={cn('checkout-order-summary', singleColumn ? 'hidden' : 'hidden md:block')}>
            <h4 className="order-summary-text mb-4">Order Summary</h4>

            <div className="order-items">
              {updatedCart.map((item, index) => {
                const max = (item as ExtendedCartItem).campaign_product.max_quantity || 999
                return (
                  <div key={index} className="order-item flex items-start gap-4 mb-5">
                    <div className="order-item-img-wrap">
                      <div className="order-item-img">
                        <img
                          src={getImageUrl(item)}
                          alt={item.campaign_product.name}
                          onError={e => setAltImg(e, item)}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <span className="order-item-qty-badge">{item.quantity}</span>
                    </div>
                    <div className="product-details grow min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h6
                            className={cn('product-name leading-tight m-0', !compact && 'product-name-link')}
                            onClick={
                              compact
                                ? undefined
                                : () => {
                                    const cp = item.campaign_product as any
                                    if (cp?.id) {
                                      leave({ productId: cp.id, slug: cp.slug })
                                    }
                                  }
                            }>
                            {item.campaign_product.name}
                          </h6>
                          {getItemVariants(item).map((variant, vIndex) => (
                            <div
                              key={vIndex}
                              className="variant-info"
                              style={{ color: 'var(--light-text-color, #6b7280)' }}>
                              {variant.name}: {variant.value}
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveClick(index)}
                          className="no-transform shrink-0 p-1 text-red-400 hover:text-red-600 transition-colors"
                          style={{ minWidth: 0, minHeight: 0, border: 'none', background: 'none' }}
                          title="Remove">
                          <Trash2 size={16} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <div className="qty-pill inline-flex items-center rounded-md overflow-hidden">
                            <button
                              type="button"
                              className="no-transform w-7 h-7 flex items-center justify-center hover:bg-black/5 disabled:opacity-30 transition-colors"
                              style={{ minWidth: 0, minHeight: 0, border: 'none', background: 'none' }}
                              disabled={item.quantity <= 1 || updatingIndex === index}
                              onClick={() => updateQuantity(index, item.quantity - 1)}>
                              <Minus size={12} />
                            </button>
                            <div
                              className="w-8 h-7 flex items-center justify-center text-sm font-medium border-x"
                              style={{ borderColor: 'var(--border-color, #d1d5db)', color: 'var(--text-color)' }}>
                              {item.quantity}
                            </div>
                            <button
                              type="button"
                              className="no-transform w-7 h-7 flex items-center justify-center hover:bg-black/5 disabled:opacity-30 transition-colors"
                              style={{ minWidth: 0, minHeight: 0, border: 'none', background: 'none' }}
                              disabled={item.quantity >= max || updatingIndex === index}
                              onClick={() => updateQuantity(index, item.quantity + 1)}>
                              <Plus size={12} />
                            </button>
                          </div>
                          {updatingIndex === index && (
                            <Loader2
                              size={16}
                              className="animate-spin"
                              style={{ color: 'var(--light-text-color, #6b7280)' }}
                            />
                          )}
                        </div>
                        <span className="text-base font-bold" style={{ color: 'var(--text-color)' }}>
                          {formatCurrency(getPrice(item), currency)}
                        </span>
                      </div>
                      {item.quantity >= max && (
                        <p className="text-xs text-red-500 mt-1 m-0">*Maximum order limit of {max}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <hr />

            <div className="checkout-totals">
              <div className="checkout-totals-line flex justify-between">
                <span>Subtotal</span>
                <span className="checkout-totals-price">{formatCurrency(totalPrice, currency)}</span>
              </div>
              <div className="checkout-totals-line flex justify-between">
                <span>Shipping fees</span>
                <span className="checkout-totals-price">
                  {Number(shipping) > 0 ? formatCurrency(Number(shipping).toString(), currency) : 'Free'}
                </span>
              </div>
              <div className="checkout-totals-line flex justify-between">
                <span>Taxes</span>
                <span className="checkout-totals-price">
                  {Number(tax) > 0 ? formatCurrency(Number(tax).toString(), currency) : formatCurrency('0', currency)}
                </span>
              </div>
              <hr />
              <div className="checkout-totals-line is-grand-total flex justify-between">
                <span>
                  <strong>Grand Total</strong> (including tax)
                </span>
                <strong>{formatCurrency(totalPriceIncTax(), currency)}</strong>
              </div>
            </div>
          </div>

          {compact && !singleColumn && completeOrderBlock}

          {/* Secure Connection badge — below Order Summary card */}
          <div className="hidden md:flex justify-center mt-4">
            <img
              src="/assets/icons/Secure-Connection.svg"
              alt="Secure Connection"
              className="w-[200px] lg:w-[240px] opacity-90"
            />
          </div>
        </div>
      </div>

      {/* Remove item confirmation */}
      <Modal
        isOpen={removeIndex !== null}
        onClose={() => {
          if (!removing) {
            setRemoveIndex(null)
          }
        }}
        title="Remove item?"
        size="sm">
        <div className="text-center py-2">
          <p className="text-base mb-6" style={{ color: 'var(--text-color)' }}>
            {removeIndex !== null && updatedCart.length <= 1
              ? 'This is the last item in your cart. Removing it will clear your cart and take you back to products.'
              : `Remove "${removeIndex !== null ? updatedCart[removeIndex]?.campaign_product?.name : ''}" from your order?`}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <button
              type="button"
              onClick={() => setRemoveIndex(null)}
              disabled={removing}
              className="btn-interactive flex-1 py-3 text-base font-semibold rounded-xl"
              style={{
                border: '2px solid var(--border-color, #d1d5db)',
                color: 'var(--text-color)',
                backgroundColor: 'transparent',
              }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmRemove}
              disabled={removing}
              className="btn-interactive flex-1 py-3 text-base font-semibold rounded-xl text-white flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--color-danger)' }}>
              {removing && <Loader2 size={16} className="animate-spin" />}
              {removing ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Checkout
